import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
    username: string;
    email?: string;  // User email address
    nickname?: string | null;  // Phase 3: Nickname
    nickname_required?: boolean;  // Phase 3: Whether nickname setup is required
    date_joined?: string;  // Profile page: Join date
    auth_source?: string;  // Profile page: Auth source (Google, Apple, Independent)
    kofi_badge?: string;  // Ko-fi contribution badge (regular, tipper, supporter, partner)
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    login: (token: string, username: string, email?: string, nickname?: string | null, nickname_required?: boolean, date_joined?: string, auth_source?: string, kofi_badge?: string) => void;
    updateNickname: (nickname: string) => void;  // Phase 3: Update nickname after modal
    refreshUser: () => Promise<void>;  // Fetch latest user data from backend
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));

    useEffect(() => {
        const checkSession = async () => {
            if (token) {
                // Determine source of truth: token vs session
                // For now, if token exists, we use stored values (legacy behavior)
                const storedUsername = localStorage.getItem('username');
                const storedEmail = localStorage.getItem('email');
                const storedNickname = localStorage.getItem('nickname');
                const storedNicknameRequired = localStorage.getItem('nickname_required');
                const storedDateJoined = localStorage.getItem('date_joined');
                const storedAuthSource = localStorage.getItem('auth_source');
                const storedKofiBadge = localStorage.getItem('kofi_badge');
                if (storedUsername) {
                    setUser({
                        username: storedUsername,
                        email: storedEmail || undefined,
                        nickname: storedNickname || undefined,
                        nickname_required: storedNicknameRequired === 'true',
                        date_joined: storedDateJoined || undefined,
                        auth_source: storedAuthSource || undefined,
                        kofi_badge: storedKofiBadge || undefined
                    });
                }
            } else {
                // No token? Check if we have a valid backend session (e.g. from Admin login)
                try {
                    const response = await fetch('/api/users/me/');
                    if (response.ok) {
                        const data = await response.json();
                        setUser({
                            username: data.username
                        });
                    }
                } catch (err) {
                    // Session check failed, user is not logged in
                    console.log("No active backend session found.");
                }
            }
        };

        checkSession();
    }, [token]);

    const login = (newToken: string, username: string, email?: string, nickname?: string | null, nickname_required?: boolean, date_joined?: string, auth_source?: string, kofi_badge?: string) => {
        localStorage.setItem('token', newToken);
        localStorage.setItem('username', username);
        if (email) localStorage.setItem('email', email);
        if (nickname) localStorage.setItem('nickname', nickname);
        if (nickname_required !== undefined) localStorage.setItem('nickname_required', String(nickname_required));
        if (date_joined) localStorage.setItem('date_joined', date_joined);
        if (auth_source) localStorage.setItem('auth_source', auth_source);
        if (kofi_badge) localStorage.setItem('kofi_badge', kofi_badge);
        setToken(newToken);
        setUser({ username, email, nickname, nickname_required, date_joined, auth_source, kofi_badge });
    };

    const updateNickname = (nickname: string) => {
        localStorage.setItem('nickname', nickname);
        localStorage.setItem('nickname_required', 'false');
        if (user) {
            setUser({ ...user, nickname, nickname_required: false });
        }
    };

    const refreshUser = async () => {
        if (!token) return;

        try {
            const response = await fetch('/api/users/me/', {
                headers: {
                    'Authorization': `Token ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                // Update both state and localStorage
                const updatedUser = {
                    username: data.username,
                    email: data.email,
                    nickname: data.nickname,
                    nickname_required: data.nickname_required,
                    date_joined: data.date_joined,
                    auth_source: data.auth_source,
                    kofi_badge: data.kofi_badge
                };

                setUser(updatedUser);
                if (data.email) localStorage.setItem('email', data.email);
                if (data.nickname) localStorage.setItem('nickname', data.nickname);
                if (data.date_joined) localStorage.setItem('date_joined', data.date_joined);
                if (data.auth_source) localStorage.setItem('auth_source', data.auth_source);
                if (data.kofi_badge) localStorage.setItem('kofi_badge', data.kofi_badge);
            }
        } catch (error) {
            console.error('Failed to refresh user data:', error);
        }
    };

    const logout = () => {
        // Clear all auth-related data
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        localStorage.removeItem('email');
        localStorage.removeItem('nickname');
        localStorage.removeItem('nickname_required');
        localStorage.removeItem('date_joined');
        localStorage.removeItem('auth_source');
        localStorage.removeItem('kofi_badge');
        setToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, token, login, updateNickname, refreshUser, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
